import { useIntersectionObserver } from 'src/common/hooks/useIntersectionObserver'
import { useAnalytics } from 'src/common/analytics'
import Section from 'src/components/shared/Section'
import { Fragment, useState } from 'react'
import Img from 'src/components/shared/Img'
import SvgIcon from 'src/components/shared/SvgIcon'
import { cn } from 'src/utils/cn'
import useDeviceDetect from 'src/common/hooks/useDeviceDetect'
import PropTypes from 'prop-types'
import { defaultValues } from './const'

export const onViewH2Id = 'facts-section'

const FactsSection = ({
  title = defaultValues.title,
  items = defaultValues.items,
  bottomText = defaultValues.bottomText,
}) => {
  const [showAll, setshowAll] = useState(false)
  const { isMobile } = useDeviceDetect()
  const { track } = useAnalytics()
  const { ref } = useIntersectionObserver({
    threshold: 0.8,
    freezeOnceVisible: true,
    onChange: (isIntersecting) => {
      if (!isIntersecting) return
      track(
        { additional_info: { section: onViewH2Id }, event_type: 'track' },
        {},
        'section_view'
      )
    },
  })

  const handleCollapsed = () => {
    setshowAll((state) => !state)
  }

  const show = isMobile ? showAll : true

  return (
    <Section className="bg-[#F5F9FF]">
      <div
        className="flex w-full flex-col items-center gap-8 font-ultramarine text-gray-555 lg:gap-14"
        data-testid="facts-stats-content"
      >
        {title && (
          <h2
            ref={ref}
            className="text-center text-heading-3 font-bold leading-[31.2px] text-gray-555 lg:text-left lg:text-heading-2 lg:leading-[41.6px]"
          >
            {"America's top debt relief company"}
          </h2>
        )}

        <div
          className="flex w-full flex-col items-center gap-0 lg:gap-14"
          data-testid="facts-stat-list"
        >
          {items.map((item, i) => (
            <div
              key={'chunk-' + i}
              className={cn(
                'flex w-full flex-col items-center justify-start xl:flex-row xl:justify-center',
                i >= 1 && !show ? 'hidden' : 'flex'
              )}
              data-testid="facts-stat-row"
            >
              {item.map((item, i2) => (
                <Fragment key={item.title}>
                  {i2 > 0 && (
                    <div className="mx-11 hidden h-[158px] w-px bg-gray-145 xl:block" />
                  )}
                  {(i2 > 0 || i > 0) && (
                    <div className="my-8 block h-px w-full bg-gray-145 xl:hidden" />
                  )}
                  <div
                    className="flex min-w-[303px] flex-col items-center gap-0.5 lg:gap-2"
                    data-testid="facts-stat-item"
                  >
                    <Img
                      src={item.imageURL}
                      alt={item.title}
                      layout="intrinsic"
                      width={70}
                      height={70}
                      className="size-[70px]"
                    />
                    <p className="whitespace-nowrap text-heading-2 font-medium leading-[41.6px] lg:text-primary-md lg:leading-[57.6px]">
                      {item.title}
                    </p>
                    <p className="text-[15px] font-normal leading-[25.5px] lg:text-body-medium lg:leading-[22.4px]">
                      {item.description}
                      {item.sup && <sup>{item.sup}</sup>}
                    </p>
                  </div>
                </Fragment>
              ))}
            </div>
          ))}
        </div>
        <button
          onClick={handleCollapsed}
          className="mx-auto my-3 flex items-center gap-x-2 text-sm font-bold text-[#3B6AF7] lg:hidden"
        >
          <span>{showAll ? 'Show less' : 'Show all'}</span>
          <SvgIcon
            name="chevronThinLeft"
            className={`size-4 stroke-[#3B6AF7] stroke-2 ${
              showAll ? 'rotate-90' : '-rotate-90'
            }`}
          />
        </button>
        {bottomText.length > 0 && (
          <div
            className="flex flex-col items-center gap-0"
            data-testid="facts-stats-disclaimer"
          >
            {bottomText.map((item) => (
              <p
                key={item}
                className="text-center text-14 leading-[19.6px] text-neutral-300 lg:text-body-medium lg:leading-[22.4px]"
              >
                {item}
              </p>
            ))}
          </div>
        )}
      </div>
    </Section>
  )
}

FactsSection.displayName = 'FactsSection'

FactsSection.propTypes = {
  title: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        imageURL: PropTypes.string,
      })
    )
  ),
  bottomText: PropTypes.arrayOf(PropTypes.string),
}

export default FactsSection
